import React, { useState, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, rad } from "../../constants/styles";
import ReceiptView from "./ReceiptView";
import Avatar from "../common/Avatar";
import TxnTable from "../common/TxnTable";
import { methodLabel, buildReceiptText, shareReceipt } from "../../utils/txnShare";
import { monthSortValue } from "../../utils/dateFormat";

function TenantPayHistoryPage() {
  const { t, h, pays, setPays, tens, props, selTid, go, flash, showConfirm } = useContext(Ctx);
  const te = tens.find(x => x.id === selTid);
  const tp = pays.filter(x => x.tid === selTid);
  const [receiptPay, setReceiptPay] = useState(null);
  const [tab, setTab] = useState("summary"); // "summary" | "txns"
  const teProp = props?.find(x => x.id === te?.pid);

  // Sort monthly cards newest-first by `p.mo` via monthSortValue — the
  // old new Date("Jun 26") approach misparsed "MMM YY" (year read as a
  // day) and fell back to alphabetical month order across year breaks.
  const sortedTp = tp.slice().sort((a, b) => monthSortValue(b.mo) - monthSortValue(a.mo));

  // Flatten all txns across all monthly rows for this tenant — feeds
  // the Transactions tab. `parentMo` is preserved so the table can
  // show which rent month each txn belonged to.
  const allTxns = tp.flatMap(p => (p.txns || []).map(tx => ({ ...tx, parentMo: p.mo })));

  const deleteHstPay = (payId) => {
    showConfirm(t.deletePayConfirm, () => {
      setPays(pays.filter(x => x.id !== payId));
      api.deletePayment(payId);
      flash(t.deleted);
    });
  };

  if (!te) return <div className="no-print" style={{ paddingTop: 16 }}><button onClick={() => go("tens")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button><p style={{ color: h.tx }}>{t.tenNotFound}</p></div>;
  if (receiptPay) return <div style={{ paddingTop: 16 }}><ReceiptView pay={receiptPay} prop={teProp} tenant={te} onBack={() => setReceiptPay(null)} /></div>;

  const handleDownload = () => {
    let text = `PAYMENT HISTORY\nProRentz\n${"-".repeat(40)}\nTenant: ${te.name}\nPhone: ${te.ph}\nDate Generated: ${new Date().toLocaleDateString()}\n\n`;
    text += `MONTH | TOTAL | STATUS | RECORDED ON | METHOD\n${"-".repeat(60)}\n`;
    tp.forEach(p => {
      const tot = p.amt + (p.com || 0);
      text += `${p.mo} | ₹${tot} | ${p.st.toUpperCase()} | ${p.dt || "N/A"} | ${(p.mth || "N/A").toUpperCase()}\n`;
      if (p.txns && p.txns.length > 0) {
        text += `  -> Transactions:\n`;
        p.txns.forEach(tx => {
          text += `     ${tx.dt} | ₹${tx.amt} | ${(tx.mth || "").toUpperCase()} | ${tx.type} | ${tx.status.toUpperCase()}\n`;
        });
      }
    });
    text += `\n${"-".repeat(40)}\nEnd of Report`;

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Payment_History_${te.name.replace(/\s/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    flash(t.reportDl);
  };

  return <div>
    {/* APP UI (HIDDEN DURING PRINT) */}
    <div className="no-print" style={{ paddingTop: 16 }}>
      <button onClick={() => go("tens")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back} to Tenants</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar src={te.avatar_url} name={te.name} size={48} bg={h.ac} fontSize={18} />
          <div>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{te.name}</h2>
            <div style={{ color: h.sc, fontSize: 12 }}>{t.payHistory}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => window.print()} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${h.bd}`, background: "transparent", color: h.sc, cursor: "pointer", fontSize: 12 }}>🖨️ Print</button>
          <button onClick={handleDownload} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}15`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"📥 " + t.download}</button>
        </div>
      </div>

      {/* Tabs: Monthly Summary | Transaction History */}
      <div style={{ display: "flex", background: h.ib, borderRadius: rad.md, padding: 3, marginBottom: 14 }}>
        {[["summary", t.monthlySummary || "Monthly Summary"], ["txns", `${t.txnHistory || "Transactions"}${allTxns.length > 0 ? ` (${allTxns.length})` : ""}`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            flex: 1, padding: "8px 4px", borderRadius: rad.sm + 2, border: "none",
            background: tab === id ? h.cd : "transparent",
            color: tab === id ? h.ac : h.ts,
            fontSize: 12, fontWeight: tab === id ? 700 : 500,
            cursor: "pointer", transition: "all 0.2s",
            boxShadow: tab === id ? h.sh : "none",
          }}>{label}</button>
        ))}
      </div>

      {tab === "summary" && <>
        {tp.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 30, color: h.sc }}>{t.noPayRecords}</div>}

        {sortedTp.map(p => <div key={p.id} style={crd(h)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{p.mo}</div><div style={{ color: h.sc, fontSize: 11 }}>{p.dt || "—"} · {p.mth || "—"}</div></div>
            <div style={{ textAlign: "right" }}><div style={{ color: h.tx, fontWeight: 700, fontSize: 15 }}>₹{(p.amt + (p.com || 0)).toLocaleString()}</div>
              <span style={bdg(p.st === "paid" ? `${h.gn}20` : p.pending ? `${h.wn}20` : `${h.rd}20`, p.st === "paid" ? h.gn : p.pending ? h.wn : h.rd)}>{p.st === "paid" ? "✓ " + t.paid : p.pending ? "⏳ " + t.pending : t.due}</span></div></div>
          {p.com > 0 && <div style={{ color: h.sc, fontSize: 11, marginTop: 6 }}>{t.rentAmt} ₹{p.amt.toLocaleString()} + {t.commonAmt} ₹{p.com}</div>}

          <div className="no-print" style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {p.st === "paid" && <button onClick={() => setReceiptPay(p)} style={{ flex: 2, padding: 8, borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}08`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"🧾 " + (t.viewReceipt || "Receipt")}</button>}
            <button onClick={() => deleteHstPay(p.id)} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.rd}15`, color: h.rd, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"✕ " + t.delete}</button>
          </div>

        </div>)}
      </>}

      {tab === "txns" && (() => {
        // Status palette + labels reused for both the rich card and the
        // list-view status pill. Kept here (instead of inside renderCard)
        // so the share-text builder also sees the same labels.
        const stColor = { approved: h.gn, verified: h.gn, pending: h.wn, rejected: h.rd };
        const stLabel = { approved: "✓ " + t.approved, verified: "✓ Verified", pending: "⏳ " + t.pending, rejected: "✕ " + t.rejected };
        const teProperty = props?.find(x => x.id === te.pid);
        const propertyAddr = teProperty?.addr || "";

        // Rich card renderer — visually matches the TxnHistory card
        // tenants see when they expand a monthly payment. Tinted
        // background by status, left border in status color, amount +
        // status badge + share button row, then label/value rows for
        // Method/Date/Type and conditional rows for remaining/approval.
        const renderRichTxnCard = (tx, i) => {
          const sc = stColor[tx.status] || h.sc;
          const onShare = (e) => {
            e.stopPropagation();
            const text = buildReceiptText(tx, { tid: te.id, mo: tx.parentMo }, te, propertyAddr, t, stLabel);
            shareReceipt(text, flash, t);
          };
          return <div key={tx.id || i} style={{
            background: `${sc}08`,
            borderRadius: 12,
            padding: 12,
            marginBottom: 8,
            borderLeft: `3px solid ${sc}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ color: h.tx, fontWeight: 700, fontSize: 15 }}>₹{(tx.amt || 0).toLocaleString()}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={bdg(`${sc}20`, sc)}>{stLabel[tx.status] || tx.status}</span>
                <button
                  type="button"
                  onClick={onShare}
                  title={t.shareReceipt || "Share receipt"}
                  aria-label={t.shareReceipt || "Share receipt"}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: h.ac, fontSize: 14, lineHeight: 1, borderRadius: 6 }}>
                  📤
                </button>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ color: h.sc, fontSize: 11 }}>{t.method}</span>
              <span style={{ color: h.tx, fontSize: 11 }}>{methodLabel(tx.mth)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ color: h.sc, fontSize: 11 }}>{t.date}</span>
              <span style={{ color: h.tx, fontSize: 11 }}>{tx.dt || "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ color: h.sc, fontSize: 11 }}>{t.type}</span>
              <span style={{ color: tx.type === "full" ? h.gn : (tx.type || "").startsWith("advance") ? h.ac : h.wn, fontSize: 11, fontWeight: 600 }}>
                {tx.type === "full" ? (t.fullPay || "Full Payment")
                  : (tx.type || "").startsWith("advance") ? (t.advancePay || "Advance Payment")
                  : (t.partialPay || "Partial Payment")}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ color: h.sc, fontSize: 11 }}>For</span>
              <span style={{ color: h.tx, fontSize: 11 }}>{tx.parentMo || "—"}</span>
            </div>
            {tx.status === "approved" && tx.approvedAt && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ color: h.sc, fontSize: 11 }}>{t.approvedOn}</span>
                <span style={{ color: h.gn, fontSize: 11 }}>{tx.approvedAt}</span>
              </div>
            )}
            {tx.status === "approved" && tx.remaining > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: h.sc, fontSize: 11 }}>{t.remaining}</span>
                <span style={{ color: h.rd, fontSize: 11, fontWeight: 600 }}>₹{tx.remaining.toLocaleString()}</span>
              </div>
            )}
            {tx.status === "rejected" && tx.rejectedAt && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: h.sc, fontSize: 11 }}>{t.rejectedOn}</span>
                <span style={{ color: h.rd, fontSize: 11 }}>{tx.rejectedAt}</span>
              </div>
            )}
            {tx.totalWas > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ color: h.sc, fontSize: 10 }}>{t.totalWas}</span>
                <span style={{ color: h.sc, fontSize: 10 }}>₹{tx.totalWas.toLocaleString()}</span>
              </div>
            )}
          </div>;
        };

        return <TxnTable
          rows={allTxns}
          h={h}
          t={t}
          title={`Transactions — ${te.name}`}
          exportFilename={`transactions-${te.name.replace(/\s+/g, "_")}`}
          dateField="dt"
          renderCard={renderRichTxnCard}
          columns={[
            { key: "dt", label: "Date", type: "date", align: "left" },
            { key: "amt", label: "Amount", type: "number", align: "right",
              format: (r) => `₹${(r.amt || 0).toLocaleString()}` },
            { key: "mth", label: "Method", type: "text",
              format: (r) => (r.mth || "—").toUpperCase() },
            { key: "type", label: "Type", type: "text",
              format: (r) => <span style={{ color: r.type === "full" ? h.gn : r.type === "advance" ? h.ac : h.wn, fontWeight: 600 }}>
                {r.type === "full" ? "Full" : r.type === "advance" ? "Adv" : "Part"}
              </span> },
            { key: "status", label: "Status", type: "text",
              format: (r) => {
                const sc = r.status === "approved" || r.status === "verified" ? h.gn
                  : r.status === "pending" ? h.wn : h.rd;
                return <span style={{ color: sc, fontWeight: 600, fontSize: 11, padding: "2px 6px", borderRadius: 6, background: `${sc}15` }}>
                  {r.status === "approved" ? "Apprvd" : r.status === "verified" ? "Verifd" : r.status === "pending" ? "Pend" : "Rejctd"}
                </span>;
              }
            },
            { key: "parentMo", label: "For", type: "date" },
          ]}
        />;
      })()}
    </div>

    {/* PRINT ONLY DOCUMENT VIEW */}
    <div className="print-only">
      <h1 style={{ textAlign: "center", borderBottom: "2px solid #000", paddingBottom: "10px" }}>ProRentz</h1>
      <h2 style={{ textAlign: "center", marginTop: "10px" }}>Payment History Report</h2>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", marginBottom: "20px" }}>
        <div>
          <p><strong>Tenant Name:</strong> {te.name}</p>
          <p><strong>Phone:</strong> {te.ph}</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <p><strong>Date Generated:</strong> {new Date().toLocaleDateString()}</p>
        </div>
      </div>

      <table className="print-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Type</th>
            <th>Status</th>
            <th>Recorded Date</th>
            <th>Method</th>
            <th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {tp.length === 0 ? (
            <tr><td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>No payments recorded yet.</td></tr>
          ) : (
            sortedTp.map((p, i) => {
              const rowTotal = p.amt + (p.com || 0);
              return (
                <React.Fragment key={p.id || i}>
                  <tr>
                    <td><strong>{p.mo}</strong></td>
                    <td>Rent + CC</td>
                    <td>{p.st.toUpperCase()}</td>
                    <td>{p.dt || "—"}</td>
                    <td>{(p.mth || "—").toUpperCase()}</td>
                    <td style={{ textAlign: "right" }}><strong>₹{rowTotal.toLocaleString()}</strong></td>
                  </tr>
                  {p.txns && p.txns.length > 0 && p.txns.map((tx, j) => (
                    <tr key={`${p.id}-tx-${j}`} className="sub-row">
                      <td style={{ paddingLeft: "30px" }}>↳ Transact.</td>
                      <td>{tx.type === "full" ? "Full" : (tx.type || "").startsWith("advance") ? "Advance" : "Partial"}</td>
                      <td>{tx.status.toUpperCase()}</td>
                      <td>{tx.dt}</td>
                      <td>{(tx.mth || "—").toUpperCase()}</td>
                      <td style={{ textAlign: "right" }}>₹{tx.amt.toLocaleString()}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      <p style={{ marginTop: "40px", textAlign: "center", fontStyle: "italic", fontSize: "10pt" }}>End of Report</p>
    </div>
  </div>;
}

export default TenantPayHistoryPage;
